import { useMutation } from '@apollo/client';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { graphql } from '@newfront-insurance/graphql-frontend';
import { useNotifications } from '@newfront-insurance/next-notifications';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { UIMessageWithMetadata } from '../../../lib/types';
import { NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../lib/analytics';

const UPSERT_MESSAGE_FEEDBACK = graphql(`
  mutation UpsertNovaMessageFeedback($messageUuid: String!, $input: MessageFeedbackInput!) {
    upsertMessageFeedback(messageUuid: $messageUuid, input: $input) {
      uuid
      messageUuid
      reason
      stage
      detail
    }
  }
`);

const FEEDBACK_REASONS = [
  { value: 'SOMETHING_BROKE', label: 'Something broke' },
  { value: 'INCOMPLETE', label: 'Incomplete' },
  { value: 'SOURCES_WRONG_OR_MISSING', label: 'Sources wrong or missing' },
  { value: 'DIDNT_FOLLOW_INSTRUCTIONS', label: "Didn't follow instructions" },
  { value: 'OUTDATED', label: 'Outdated' },
  { value: 'INACCURATE', label: 'Inaccurate' },
] as const;

const FEEDBACK_ACTION_GRADIENT = 'bg-[linear-gradient(150deg,#97ECE3_2%,#DBC1F4_82%)]';

type FeedbackReason = (typeof FEEDBACK_REASONS)[number]['value'];

interface MessageFeedbackPopoverProps {
  message: UIMessageWithMetadata;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MessageFeedbackPopover({ message, open, onOpenChange }: MessageFeedbackPopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const firstReasonRef = useRef<HTMLButtonElement>(null);
  const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const [selectedReason, setSelectedReason] = useState<FeedbackReason>();
  const [detail, setDetail] = useState('');
  const [isSavingReason, setIsSavingReason] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [upsertFeedback] = useMutation(UPSERT_MESSAGE_FEEDBACK);
  const notifications = useNotifications();
  const analytics = useAnalytics();
  const isProblemReport = selectedReason === 'SOMETHING_BROKE';

  useEffect(() => {
    if (!open) return undefined;
    const focusTimer = setTimeout(() => firstReasonRef.current?.focus(), 0);
    const handlePointerDown = (event: PointerEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) onOpenChange(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [onOpenChange, open]);

  const persistFeedback = (reason: FeedbackReason, stage: 'CAPTURED' | 'SUBMITTED', nextDetail?: string) =>
    upsertFeedback({
      variables: {
        messageUuid: message.id,
        input: {
          reason,
          stage,
          detail: nextDetail?.trim() || null,
        },
      },
    });

  const captureReason = (reason: FeedbackReason) => {
    setSelectedReason(reason);
    setDetail('');
    setError(undefined);
    setIsSavingReason(true);
    const save = saveQueueRef.current
      .catch(() => undefined)
      .then(() => persistFeedback(reason, 'CAPTURED'))
      .then(() => {
        trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.MESSAGE_FEEDBACK_REASON_CAPTURED, {
          messageUuid: message.id,
          reason,
        });
      })
      .catch(() => setError("We couldn't save that selection. Try again."))
      .finally(() => setIsSavingReason(false));
    saveQueueRef.current = save;
  };

  const handleSubmit = async () => {
    if (!selectedReason || isSubmitting) return;
    setError(undefined);
    setIsSubmitting(true);
    try {
      await saveQueueRef.current.catch(() => undefined);
      const result = await persistFeedback(selectedReason, 'SUBMITTED', detail);
      const feedbackUuid = result.data?.upsertMessageFeedback.uuid;
      if (!feedbackUuid) throw new Error('Feedback save returned no identifier');

      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.MESSAGE_FEEDBACK_SUBMITTED, {
        hasDetail: Boolean(detail.trim()),
        messageUuid: message.id,
        reason: selectedReason,
      });

      notifications.add({
        type: 'success',
        duration: 3000,
        title: isProblemReport
          ? "Thanks, we're on it. We'll email you when it's fixed."
          : 'Thanks, your feedback was saved.',
      });
      onOpenChange(false);
    } catch {
      setError(isProblemReport ? 'The problem report could not be sent. Try again.' : 'Feedback could not be saved.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <motion.div
      ref={panelRef}
      role="dialog"
      aria-label="What went wrong?"
      aria-modal="false"
      initial={{ opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.98 }}
      transition={{ duration: 0.16, ease: 'easeOut' }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onOpenChange(false);
        }
      }}
      className="border-steel-200 absolute top-full left-0 z-50 mt-2 w-[min(28.75rem,calc(100vw-2rem))] origin-top-left rounded-[0.25rem] border bg-white shadow-xl"
    >
      <div className="p-4">
        <h3 className="text-sm leading-5 font-semibold text-black">What went wrong?</h3>
        <div
          className="mt-2.5 flex flex-wrap gap-x-0.5 gap-y-2.5"
          role="radiogroup"
          aria-label="Feedback reason"
          onKeyDown={(event) => {
            if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
            const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]'));
            const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
            if (currentIndex < 0) return;
            event.preventDefault();
            const nextIndex =
              event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? buttons.length - 1
                  : (currentIndex + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + buttons.length) %
                    buttons.length;
            buttons[nextIndex]?.focus();
          }}
        >
          {FEEDBACK_REASONS.map((reason, index) => (
            <button
              key={reason.value}
              ref={index === 0 ? firstReasonRef : undefined}
              type="button"
              role="radio"
              aria-checked={selectedReason === reason.value}
              onClick={() => captureReason(reason.value)}
              className={cn(
                'h-8 rounded-full border px-3 text-sm leading-5 transition-opacity',
                'focus-visible:outline-wtw-infinity-500 focus-visible:outline-2 focus-visible:outline-offset-2',
                selectedReason === reason.value
                  ? cn('border-transparent text-[#350E5E]', FEEDBACK_ACTION_GRADIENT)
                  : 'border-steel-200 text-steel-400 hover:border-steel-300 bg-white',
              )}
            >
              {reason.label}
            </button>
          ))}
        </div>
      </div>

      {selectedReason && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="border-steel-200 mt-2.5 border-t p-4"
        >
          <label htmlFor={`nova-feedback-detail-${message.id}`} className="text-sm leading-5 font-semibold text-black">
            {isProblemReport ? 'What happened?' : 'What would make it better? (optional)'}
          </label>
          <textarea
            id={`nova-feedback-detail-${message.id}`}
            value={detail}
            rows={3}
            maxLength={4000}
            onChange={(event) => {
              setDetail(event.target.value);
              setError(undefined);
            }}
            placeholder={
              isProblemReport
                ? 'e.g. The response stopped generating halfway through'
                : 'Nova will rewrite this answer using your feedback.'
            }
            className={cn(
              'border-steel-200 text-steel-500 placeholder:text-steel-300 mt-2.5 h-[6.25rem] w-full resize-none',
              'focus:border-steel-400 rounded-[0.75rem] border p-3 text-sm leading-5 outline-none',
            )}
          />
          {error && (
            <div className="mt-2 flex items-center justify-between gap-3" role="alert">
              <p className="text-fire-600 text-xs">{error}</p>
              {selectedReason && error.includes("couldn't save") && (
                <button
                  type="button"
                  className="text-brand-400 text-xs font-medium"
                  onClick={() => captureReason(selectedReason)}
                >
                  Retry
                </button>
              )}
            </div>
          )}
          <div className="mt-2.5 flex justify-end">
            <button
              type="button"
              disabled={isSubmitting || isSavingReason}
              onClick={() => void handleSubmit()}
              className={cn(
                'inline-flex h-9 min-w-[10.375rem] items-center justify-center gap-2 rounded-full px-4',
                'text-sm leading-5 font-semibold text-[#350E5E] transition-opacity hover:opacity-90',
                'disabled:cursor-not-allowed disabled:opacity-50',
                FEEDBACK_ACTION_GRADIENT,
              )}
            >
              {isSubmitting ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <ArrowRight className="size-4" aria-hidden="true" />
              )}
              {isProblemReport ? 'Report problem' : 'Fix this answer'}
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
